<template>
  <div v-if="isLoading || isGettingToken" class="auth-loading-container">
    <div class="auth-loading-content">
      <div class="logo-container">
        <img src="@/assets/viaphoton-logo-dark.svg" alt="ViaPhoton Logo" class="logo-image" />
      </div>
      <AuthLoader />
      <p class="loading-text">{{ loadingText }}</p>
    </div>
  </div>

  <router-view v-else />
</template>

<script lang="ts">
  import { defineComponent, watchEffect, ref } from 'vue';
  import { useAuth0 } from '@auth0/auth0-vue';
  import { useAuthStore } from '@/store/authStore';
  import { useAccessStore } from '@/store/accessStore';
  import logger from '@/services/logger';
  import type { User } from '@/types/auth';
  import AuthLoader from '@/components/common/AuthLoader.vue';

  export default defineComponent({
    name: 'App',
    components: {
      AuthLoader,
    },

    setup() {
      const { isAuthenticated, user, getAccessTokenSilently, isLoading } = useAuth0();
      const authStore = useAuthStore();
      const accessStore = useAccessStore();
      const isGettingToken = ref(false);
      const loadingText = ref('Just a moment...');

      async function syncSession(): Promise<void> {
        if (isAuthenticated.value && user.value) {
          authStore.setUser(user.value as User);

          try {
            isGettingToken.value = true;
            const token = await getAccessTokenSilently();
            authStore.setCredential({ token });
            void accessStore.load();
          } catch (tokenError) {
            logger.error('Error getting token:', tokenError);
            accessStore.fail("Couldn't complete sign-in. Retry, or sign out and back in.");
          } finally {
            isGettingToken.value = false;
          }
        } else {
          authStore.logout();
          accessStore.reset();
        }
      }

      // syncSession handles its own errors; the reactive reads before its first await are tracked.
      watchEffect(() => {
        void syncSession();
      });

      return {
        isLoading,
        isGettingToken,
        loadingText,
      };
    },
  });
</script>

<style>
  .auth-loading-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  }

  .auth-loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }

  .logo-image {
    width: 200px;
    height: auto;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  }

  .loading-text {
    font-size: 18px;
    color: #495057;
    margin: 0;
    font-weight: 500;
    letter-spacing: 0.5px;
  }
</style>
